import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

export const blue = '#0d4efe';
export const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';

export const StyledAdminBusinessEditDialog = styled(Dialog)(() => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.52)',
    backdropFilter: 'blur(4px)',
  },
  '& .MuiDialog-paper': {
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgba(13, 78, 254, 0.14)',
    boxShadow:
      '0 24px 56px rgba(13, 78, 254, 0.16), 0 8px 24px rgba(0, 0, 0, 0.06)',
    background: '#ffffff',
    maxWidth: 520,
    width: '100%',
  },
}));

export const AdminBusinessEditModalTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  margin: 0,
  padding: theme.spacing(2.5, 3),
  paddingRight: theme.spacing(6),
  paddingBottom: theme.spacing(2),
  position: 'relative',
  background:
    'linear-gradient(135deg, rgba(1, 235, 178, 0.14) 0%, rgba(13, 78, 254, 0.1) 55%, rgba(13, 78, 254, 0.04) 100%)',
  borderBottom: '1px solid rgba(13, 78, 254, 0.1)',
  fontWeight: 700,
  fontSize: '1.125rem',
  lineHeight: 1.35,
  letterSpacing: '-0.02em',
  color: ink,
  '& .admin-business-edit-icon-wrap': {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(145deg, rgba(1, 235, 178, 0.2) 0%, rgba(13, 78, 254, 0.15) 100%)',
    border: '1px solid rgba(13, 78, 254, 0.18)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
  },
  '& .admin-business-edit-icon': {
    fontSize: 26,
    color: blue,
  },
}));

export const AdminBusinessEditModalCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: muted,
  borderRadius: 10,
  '&:hover': {
    background: 'rgba(13, 78, 254, 0.08)',
    color: ink,
  },
}));

export const AdminBusinessEditModalContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const AdminBusinessEditModalActions = styled(DialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2, 3, 2.5),
  gap: theme.spacing(1.25),
  background:
    'linear-gradient(180deg, rgba(247, 250, 252, 0.95) 0%, rgba(240, 244, 248, 0.98) 100%)',
  borderTop: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const AdminBusinessEditCancelButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '8px 18px',
  color: ink,
  borderColor: 'rgba(13, 78, 254, 0.35)',
  '&:hover': {
    borderColor: blue,
    background: 'rgba(13, 78, 254, 0.06)',
  },
}));

export const AdminBusinessEditSaveButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 10,
  padding: '8px 22px',
  color: '#fff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    opacity: 0.93,
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.42)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(255, 255, 255, 0.65)',
    boxShadow: 'none',
    opacity: 1,
  },
}));

/** Group future admin fields under titled sections. */
export const AdminBusinessEditSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '16px 18px',
  borderRadius: 12,
  background: '#f7fafc',
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));

export const AdminBusinessEditSectionTitle = styled(Typography)(() => ({
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#8a94a6',
}));

export const AdminBusinessEditSectionDescription = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  lineHeight: 1.55,
  color: muted,
  marginTop: -4,
}));

export const AdminBusinessEditField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: '#fff',
    fontSize: '0.875rem',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(13, 78, 254, 0.35)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: blue,
    borderWidth: '1.5px',
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: '0 0 0 3px rgba(13, 78, 254, 0.08)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: blue,
  },
}));

export const AdminBusinessEditHint = styled(Typography)(() => ({
  fontSize: '0.75rem',
  lineHeight: 1.5,
  color: muted,
}));

export const AdminBusinessEditRatingRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}));
